// SCR-STAFF-602 (بخش پرونده) — فرم‌های مراجعه‌کننده (US-DOC-030 تا 032): فرم‌های تکمیل‌شده با وضعیت و منبع، شروع فرم، ارسال لینک پیش از
// مراجعه (OTP موبایل اصلی؛ پیام با MSG) و «تحویل به مراجعه‌کننده» (حالت تبلت با PIN کارکن — FLOW-09).
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormGroup from '@mui/material/FormGroup';
import MenuItem from '@mui/material/MenuItem';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { unwrap, type DocFormRequestView, type DocFormScheduleView, type DocFormSubmissionView } from '@phoenix/api-client';
import { useAccess, useIdempotencyKey, useTenantSession } from '@phoenix/auth';
import { useTranslation } from '@phoenix/i18n';
import { DataTable, EmptyState, ErrorState, FormDialog, JalaliDate, latinDigits, SampleBadge, type DataTableColumn } from '@phoenix/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useNavigate } from 'react-router';
import { docKeys, SCR_STAFF_602, SubmissionStatusChip } from './common';
import { extraFieldsOf, usePublishedConsents, usePublishedForms } from './hooks';

function StartFormDialog({ patientId, onClose }: { patientId: string; onClose: () => void }) {
  const { t } = useTranslation('doc');
  const { tenantApi } = useTenantSession();
  const navigate = useNavigate();
  const forms = usePublishedForms();
  const [templateId, setTemplateId] = useState('');
  const start = useMutation({
    mutationFn: () => unwrap(tenantApi.POST('/api/v1/documents/form-submissions', { body: { templateId, patientId, appointmentId: null, encounterId: null, tabletSessionId: null } })),
    onSuccess: (detail) => navigate(`/documents/forms/${detail.submission.id}`),
  });
  return (
    <FormDialog open titleKey="doc:forms.start_title" screenId={SCR_STAFF_602} onSubmit={() => templateId && start.mutate()} onClose={onClose} isDirty={templateId !== ''} isSubmitting={start.isPending} submitError={start.error}>
      <TextField select label={t('doc:forms.template')} value={templateId} onChange={(event) => setTemplateId(event.target.value)} slotProps={{ htmlInput: { 'data-testid': 'form-start-template' } }} required>
        {(forms.data?.items ?? []).map((item) => (
          <MenuItem key={item.id} value={item.id}>
            {item.name}
          </MenuItem>
        ))}
      </TextField>
    </FormDialog>
  );
}

function RequestDialog({ patientId, onClose }: { patientId: string; onClose: () => void }) {
  const { t } = useTranslation('doc');
  const { tenantApi, session } = useTenantSession();
  const queryClient = useQueryClient();
  const forms = usePublishedForms();
  const [templateId, setTemplateId] = useState('');
  const send = useMutation({
    mutationFn: () => unwrap(tenantApi.POST('/api/v1/documents/form-requests', { body: { templateId, patientId, appointmentId: null, expiresAt: null } })),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: docKeys.requests(session?.tenantId, patientId) });
      onClose();
    },
  });
  return (
    <FormDialog open titleKey="doc:forms.request_title" screenId={SCR_STAFF_602} onSubmit={() => templateId && send.mutate()} onClose={onClose} isDirty={templateId !== ''} isSubmitting={send.isPending} submitError={send.error}>
      <Alert severity="info">{t('doc:forms.request_hint')}</Alert>
      <TextField select label={t('doc:forms.template')} value={templateId} onChange={(event) => setTemplateId(event.target.value)} slotProps={{ htmlInput: { 'data-testid': 'form-request-template' } }} required>
        {(forms.data?.items ?? []).map((item) => (
          <MenuItem key={item.id} value={item.id}>
            {item.name}
          </MenuItem>
        ))}
      </TextField>
    </FormDialog>
  );
}

/** «تحویل به مراجعه‌کننده» (AC-DOC-031.1، 031.2): انتخاب فرم‌ها و رضایت‌نامه‌ها به ترتیب و PIN خروج. */
export function TabletHandoverDialog({ patientId, appointmentId, onClose }: { patientId: string; appointmentId?: string; onClose: () => void }) {
  const { t } = useTranslation('doc');
  const { tenantApi } = useTenantSession();
  const navigate = useNavigate();
  const access = useAccess();
  const forms = usePublishedForms(access.can('doc.form.fill'));
  const consents = usePublishedConsents(access.can('doc.consent.collect'));
  const [items, setItems] = useState<{ kind: 'Form' | 'Consent'; templateId: string }[]>([]);
  const [pin, setPin] = useState('');
  const [values, setValues] = useState<Record<string, Record<string, string>>>({});
  const [error, setError] = useState<string | null>(null);
  const start = useMutation({
    mutationFn: () => unwrap(tenantApi.POST('/api/v1/documents/tablet-sessions', { body: { patientId, appointmentId: appointmentId ?? null, items, pin } })),
    onSuccess: (session) => navigate(`/tablet/${session.id}`, { state: { values } }),
  });
  const toggle = (kind: 'Form' | 'Consent', templateId: string, checked: boolean) =>
    setItems((current) => (checked ? [...current, { kind, templateId }] : current.filter((item) => !(item.kind === kind && item.templateId === templateId))));

  return (
    <FormDialog
      open
      titleKey="doc:tablet.handover_title"
      screenId={SCR_STAFF_602}
      onSubmit={() => {
        if (items.length === 0 || !/^\d{4,8}$/.test(pin)) {
          setError(t('doc:tablet.handover_invalid'));
          return;
        }
        start.mutate();
      }}
      onClose={onClose}
      isDirty={items.length > 0}
      isSubmitting={start.isPending}
      submitError={start.error}
    >
      <Typography variant="subtitle2">{t('doc:tablet.items')}</Typography>
      <FormGroup data-testid="tablet-items">
        {(forms.data?.items ?? []).map((item) => (
          <FormControlLabel
            key={`f-${item.id}`}
            control={<Checkbox onChange={(event) => toggle('Form', item.id, event.target.checked)} slotProps={{ input: { 'data-testid': `tablet-item-form-${item.id}` } as never }} />}
            label={
              <>
                {t('doc:tablet.form_item', { name: item.name })} {item.isSample ? <SampleBadge /> : null}
              </>
            }
          />
        ))}
        {(consents.data?.items ?? []).map((item) => (
          <FormControlLabel
            key={`c-${item.id}`}
            control={<Checkbox onChange={(event) => toggle('Consent', item.id, event.target.checked)} slotProps={{ input: { 'data-testid': `tablet-item-consent-${item.id}` } as never }} />}
            label={
              <>
                {t('doc:tablet.consent_item', { name: item.name })} {item.isSample ? <SampleBadge /> : null}
              </>
            }
          />
        ))}
      </FormGroup>
      {items
        .filter((item) => item.kind === 'Consent')
        .map((item) => {
          const template = consents.data?.items.find((candidate) => candidate.id === item.templateId);
          return extraFieldsOf(template).map((field) => (
            <TextField
              key={`${item.templateId}-${field.key}`}
              label={`${template?.name ?? ''} · ${field.label}`}
              required={field.required}
              value={values[item.templateId]?.[field.key] ?? ''}
              onChange={(event) => setValues((current) => ({ ...current, [item.templateId]: { ...current[item.templateId], [field.key]: event.target.value } }))}
              slotProps={{ htmlInput: { 'data-testid': `tablet-value-${field.key}` } }}
            />
          ));
        })}
      <TextField
        type="password"
        label={t('doc:tablet.pin')}
        helperText={t('doc:tablet.pin_hint')}
        value={pin}
        onChange={(event) => setPin(latinDigits(event.target.value).replace(/\D/g, ''))}
        slotProps={{ htmlInput: { 'data-testid': 'tablet-pin', inputMode: 'numeric', maxLength: 8, autoComplete: 'new-password' } }}
        required
      />
      {error ? <Alert severity="error">{error}</Alert> : null}
    </FormDialog>
  );
}

/** ارسال خودکار پرسشنامه پیش از هر جلسه (AC-CLN-116.1؛ فرم پیش از مراجعه DOC با لینک امن تا زمان نوبت). */
function ScheduleDialog({ patientId, onClose }: { patientId: string; onClose: () => void }) {
  const { t } = useTranslation('doc');
  const { tenantApi, session } = useTenantSession();
  const queryClient = useQueryClient();
  const key = useIdempotencyKey();
  const forms = usePublishedForms();
  const [templateId, setTemplateId] = useState('');
  const save = useMutation({
    mutationFn: () => unwrap(tenantApi.POST('/api/v1/documents/form-schedules', { headers: { 'Idempotency-Key': key.current() }, body: { patientId, templateId, providerId: null } })),
    onSettled: (_data, error) => key.settle(error ?? undefined),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['documents', session?.tenantId, 'form-schedules', patientId] });
      onClose();
    },
  });
  return (
    <FormDialog open titleKey="doc:schedules.add_title" screenId={SCR_STAFF_602} onSubmit={() => templateId && save.mutate()} onClose={onClose} isDirty={templateId !== ''} isSubmitting={save.isPending} submitError={save.error}>
      <Alert severity="info">{t('doc:schedules.hint')}</Alert>
      <TextField select required label={t('doc:forms.template')} value={templateId} onChange={(event) => setTemplateId(event.target.value)} slotProps={{ htmlInput: { 'data-testid': 'form-schedule-template' } }}>
        {(forms.data?.items ?? []).map((item) => (
          <MenuItem key={item.id} value={item.id}>
            {item.name}
          </MenuItem>
        ))}
      </TextField>
    </FormDialog>
  );
}

export function PatientFormsPanel({ patientId }: { patientId: string }) {
  const { t } = useTranslation('doc');
  const { tenantApi, session } = useTenantSession();
  const access = useAccess();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [starting, setStarting] = useState(false);
  const [requesting, setRequesting] = useState(false);
  const [handover, setHandover] = useState(false);
  const submissions = useQuery({
    queryKey: docKeys.submissions(session?.tenantId, patientId),
    queryFn: () => unwrap(tenantApi.GET('/api/v1/documents/form-submissions', { params: { query: { patientId, limit: 50 } } })),
  });
  const canFill = access.can('doc.form.fill');
  const requests = useQuery({
    queryKey: docKeys.requests(session?.tenantId, patientId),
    queryFn: () => unwrap(tenantApi.GET('/api/v1/documents/form-requests', { params: { query: { patientId } } })),
    enabled: canFill,
  });
  const [scheduling, setScheduling] = useState(false);
  const schedules = useQuery({
    queryKey: ['documents', session?.tenantId, 'form-schedules', patientId],
    queryFn: () => unwrap(tenantApi.GET('/api/v1/documents/form-schedules', { params: { query: { patientId } } })),
    enabled: canFill,
  });
  const stop = useMutation({
    mutationFn: (id: string) => unwrap(tenantApi.POST('/api/v1/documents/form-schedules/{id}:stop', { params: { path: { id } } })),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['documents', session?.tenantId, 'form-schedules', patientId] }),
  });
  const cancel = useMutation({
    mutationFn: (id: string) => unwrap(tenantApi.POST('/api/v1/documents/form-requests/{id}:cancel', { params: { path: { id } } })),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: docKeys.requests(session?.tenantId, patientId) }),
  });

  const columns: DataTableColumn<DocFormSubmissionView>[] = [
    { id: 'name', headerKey: 'doc:forms.columns.name', cell: (row) => row.templateName },
    { id: 'status', headerKey: 'doc:forms.columns.status', cell: (row) => <SubmissionStatusChip status={row.status} testId={`form-status-${row.id}`} /> },
    { id: 'source', headerKey: 'doc:forms.columns.source', cell: (row) => t(`doc:source.${row.source}`, { defaultValue: row.source }) },
    { id: 'date', headerKey: 'doc:forms.columns.date', cell: (row) => <JalaliDate value={row.submittedAt ?? row.startedAt} withTime /> },
    {
      id: 'actions',
      headerKey: 'doc:forms.columns.actions',
      cell: (row) => (
        <Button size="small" onClick={() => navigate(`/documents/forms/${row.id}`)} data-testid={`form-open-${row.id}`}>
          {t('doc:actions.open')}
        </Button>
      ),
    },
  ];
  const requestColumns: DataTableColumn<DocFormRequestView>[] = [
    { id: 'name', headerKey: 'doc:forms.columns.name', cell: (row) => row.templateName ?? '—' },
    { id: 'status', headerKey: 'doc:forms.columns.status', cell: (row) => <span data-testid={`form-request-status-${row.id}`}>{t(`doc:request_status.${row.status}`, { defaultValue: row.status })}</span> },
    { id: 'expires', headerKey: 'doc:forms.columns.expires', cell: (row) => <JalaliDate value={row.expiresAt} withTime /> },
    {
      id: 'actions',
      headerKey: 'doc:forms.columns.actions',
      cell: (row) =>
        row.status === 'Requested' || row.status === 'Opened' ? (
          <Button size="small" color="warning" onClick={() => cancel.mutate(row.id)} data-testid={`form-request-cancel-${row.id}`}>
            {t('doc:actions.cancel')}
          </Button>
        ) : null,
    },
  ];

  const scheduleColumns: DataTableColumn<DocFormScheduleView>[] = [
    { id: 'name', headerKey: 'doc:forms.columns.name', cell: (row) => row.templateName ?? '—' },
    { id: 'since', headerKey: 'doc:schedules.since', cell: (row) => <JalaliDate value={row.startedAt} /> },
    {
      id: 'actions',
      headerKey: 'doc:forms.columns.actions',
      cell: (row) =>
        row.isActive ? (
          <Button size="small" color="warning" onClick={() => stop.mutate(row.id)} data-testid={`form-schedule-stop-${row.id}`}>
            {t('doc:schedules.stop')}
          </Button>
        ) : (
          t('doc:schedules.stopped')
        ),
    },
  ];

  return (
    <Stack spacing={2} data-screen-id={SCR_STAFF_602} data-testid="patient-forms">
      {canFill ? (
        <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap' }}>
          <Button variant="contained" onClick={() => setStarting(true)} data-testid="form-start">
            {t('doc:forms.start')}
          </Button>
          <Button variant="outlined" onClick={() => setRequesting(true)} data-testid="form-request">
            {t('doc:forms.request')}
          </Button>
          <Button variant="outlined" onClick={() => setHandover(true)} data-testid="tablet-handover">
            {t('doc:tablet.handover')}
          </Button>
          <Button variant="outlined" onClick={() => setScheduling(true)} data-testid="form-schedule">
            {t('doc:schedules.add')}
          </Button>
        </Stack>
      ) : null}
      {submissions.error ? <ErrorState error={submissions.error} /> : null}
      {submissions.data && submissions.data.items.length === 0 ? <EmptyState titleKey="doc:forms.empty" /> : null}
      {submissions.data && submissions.data.items.length > 0 ? (
        <DataTable captionKey="doc:forms.title" columns={columns} rows={submissions.data.items} getRowId={(row) => row.id} />
      ) : null}
      {canFill && requests.data && requests.data.items.length > 0 ? (
        <Paper variant="outlined" sx={{ p: 2 }}>
          <Typography variant="subtitle1" gutterBottom>
            {t('doc:forms.requests')}
          </Typography>
          <DataTable captionKey="doc:forms.requests" columns={requestColumns} rows={requests.data.items} getRowId={(row) => row.id} />
        </Paper>
      ) : null}
      {canFill && schedules.data && schedules.data.items.some((item) => item.isActive) ? (
        <Paper variant="outlined" sx={{ p: 2 }} data-testid="form-schedules">
          <Typography variant="subtitle1" gutterBottom>
            {t('doc:schedules.title')}
          </Typography>
          <DataTable captionKey="doc:schedules.title" columns={scheduleColumns} rows={schedules.data.items.filter((item) => item.isActive)} getRowId={(row) => row.id} />
        </Paper>
      ) : null}
      {cancel.error ? <ErrorState error={cancel.error} compact /> : null}
      {stop.error ? <ErrorState error={stop.error} compact /> : null}
      {scheduling ? <ScheduleDialog patientId={patientId} onClose={() => setScheduling(false)} /> : null}
      {starting ? <StartFormDialog patientId={patientId} onClose={() => setStarting(false)} /> : null}
      {requesting ? <RequestDialog patientId={patientId} onClose={() => setRequesting(false)} /> : null}
      {handover ? <TabletHandoverDialog patientId={patientId} onClose={() => setHandover(false)} /> : null}
    </Stack>
  );
}
